import { ViewportScroller } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet, Scroll } from '@angular/router';
import { BaseComponent } from '@shared/components/base-component/base.component';
import { SpinnerService } from '@shared/components/spinner/service/spinner.service';
import { DatepickerI18nService } from '@shared/services/localisation/datepicker-i18n.service';
import { imageViewerRoute, maintenancePageRoute } from '@utils/routing.utils';
import { filter } from 'rxjs/operators';
import { CurrentUserService } from '@src/app/features/authentication/services/current-user.service';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { NavigationComponent } from '@shared/components/navigation/navigation.component';
import { FooterComponent } from '@shared/components/footer/footer.component';

export const supportLanguages = ['English', 'Español', 'Français', 'Indonesia', 'Português', 'Tiếng Việt', '中文'];

@Component({
  selector: 'stx-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SpinnerComponent, NavigationComponent, RouterOutlet, FooterComponent]
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy {
  private matIconRegistry = inject(MatIconRegistry);
  private domSanitizer = inject(DomSanitizer);
  private currentUserService = inject(CurrentUserService);
  spinnerService = inject(SpinnerService);
  private changeRef = inject(ChangeDetectorRef);
  private router = inject(Router);
  private viewportScroller = inject(ViewportScroller);
  private datepickerI18nService = inject(DatepickerI18nService);

  isLoggedIn: boolean;
  hideNavigation = true;
  showSpinner = false;
  private routesWithHiddenNavigation = [imageViewerRoute, maintenancePageRoute];

  constructor() {
    super();
    this.scrollToTop();
    this.changeLayoutOnRouterEvent();
  }

  ngOnInit() {
    this.initializeIcons();

    this.subSink.sink = this.currentUserService.user$.subscribe(user => {
      this.isLoggedIn = !!user;
      this.recomputeHideNavigationFromUrl(this.router.url);
      this.changeRef.detectChanges();
    });

    this.subSink.sink = this.spinnerService.activeTasks.subscribe(activeTask => {
      this.showSpinner = activeTask;
      this.changeRef.detectChanges();
    });

    this.subSink.sink = this.datepickerI18nService.followLocaleUpdatesAndGetSubscription();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  private scrollToTop() {
    this.subSink.sink = this.router.events
      .pipe(filter((e: unknown): e is Scroll => e instanceof Scroll))
      .subscribe((scrollEvent: Scroll) => {
        if (scrollEvent.position) {
          this.viewportScroller.scrollToPosition(scrollEvent.position);
        } else if (scrollEvent.anchor) {
          this.viewportScroller.scrollToAnchor(scrollEvent.anchor);
        } else {
          this.viewportScroller.scrollToPosition([0, 0]);
        }
      });
  }

  private changeLayoutOnRouterEvent() {
    this.subSink.sink = this.router.events.pipe(filter((e: any) => e instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
      this.recomputeHideNavigationFromUrl(e.url);
    });
  }

  private recomputeHideNavigationFromUrl(url: string) {
    this.hideNavigation = !this.isLoggedIn;
    const splitUrl = url.split('/');
    if (splitUrl.length > 1) {
      const firstRoute = splitUrl[1].split('?')[0];
      if (this.routesWithHiddenNavigation.includes(firstRoute)) {
        this.hideNavigation = true;
        return;
      }
    }
  }

  private initializeIcons(): void {
    this.matIconRegistry.addSvgIcon('upload', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/upload.svg'));
    this.matIconRegistry.addSvgIcon('fb', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/post-facebook.svg'));
    this.matIconRegistry.addSvgIcon('insta', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/post-instagram.svg'));
    this.matIconRegistry.addSvgIcon('twitter', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/post-twitter.svg'));
    this.matIconRegistry.addSvgIcon('snap', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/snapchat.svg'));
    this.matIconRegistry.addSvgIcon('yt', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/youtube.svg'));
    this.matIconRegistry.addSvgIcon(
      'lang-switcher',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/language-switcher.svg')
    );
    this.matIconRegistry.addSvgIcon('static-doc', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/list_alt-24px.svg'));
    this.matIconRegistry.addSvgIcon('creation-date', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/creation-date.svg'));
    this.matIconRegistry.addSvgIcon('modified-date', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/modified-date.svg'));
    this.matIconRegistry.addSvgIcon(
      'submission-date',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/submission-date.svg')
    );
    this.matIconRegistry.addSvgIcon('tablet-sign', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/tablet-sign.svg'));
    this.matIconRegistry.addSvgIcon('download', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/download.svg'));
    this.matIconRegistry.addSvgIcon('upload-alt', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/upload-alt.svg'));
    this.matIconRegistry.addSvgIcon('contract', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/contract.svg'));
    this.matIconRegistry.addSvgIcon('printer', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/printer.svg'));
    this.matIconRegistry.addSvgIcon('unlink', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/unlink.svg'));
    this.matIconRegistry.addSvgIcon('today', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/today.svg'));
    this.matIconRegistry.addSvgIcon('pin', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/pin.svg'));
  }
}
